<template>
  <teleport to="body">
    <div class="modal-overlay" @click="$emit('close')">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="$emit('close')">×</button>

        <div class="premium-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
          </svg>
        </div>

        <h2>Unlock Premium Features</h2>

        <p class="feature-highlight" v-if="feature">
          {{ featureMessages[feature] || 'Get access to premium features' }}
        </p>

        <ul class="benefits-list">
          <li>
            <span class="benefit-icon">&#9829;</span>
            Save your favorite songs
          </li>
          <li>
            <span class="benefit-icon">&#8634;</span>
            Access your song history
          </li>
          <li>
            <span class="benefit-icon">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.33a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z"/></svg>
            </span>
            Set a custom display name
          </li>
          <li>
            <span class="benefit-icon">&#9733;</span>
            Show your subscriber badge
          </li>
        </ul>

        <div class="price-section">
          <span class="price">$3</span>
          <span class="period">/month</span>
        </div>

        <button
          class="upgrade-button"
          @click="handleUpgrade"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="loading-spinner"></span>
          <span v-else>Go Premium</span>
        </button>

        <p v-if="error" class="error-message">{{ error }}</p>

        <p class="cancel-note">Cancel anytime. No commitment.</p>
      </div>
    </div>
  </teleport>
</template>

<script>
import { ref } from 'vue'
import { useSubscription } from '../composables/useSubscription'
import { useAuth } from '../composables/useAuth'

export default {
  name: 'PremiumUpsellModal',
  props: {
    feature: {
      type: String,
      default: null
    }
  },
  emits: ['close', 'require-sign-in'],
  setup(props, { emit }) {
    const { goToPremium, error: subscriptionError } = useSubscription()
    const { isLoggedIn } = useAuth()
    const isLoading = ref(false)
    const error = ref(null)

    const featureMessages = {
      favorites: 'Save songs to your favorites for quick access',
      history: 'Track your recently sung songs',
      displayName: 'Set a custom display name that everyone sees',
      badge: 'Show off your subscriber badge'
    }

    const handleUpgrade = async () => {
      if (!isLoggedIn.value) {
        emit('require-sign-in')
        return
      }
      isLoading.value = true
      error.value = null

      try {
        await goToPremium()
        // Redirect happens in goToPremium, modal will unmount
      } catch (err) {
        error.value = subscriptionError.value || 'Something went wrong. Please try again.'
        isLoading.value = false
      }
    }

    return {
      isLoading,
      error,
      featureMessages,
      handleUpgrade
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.modal-content {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: linear-gradient(180deg, rgba(40, 30, 10, 0.98) 0%, rgba(26, 26, 26, 0.98) 100%);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 2rem;
  max-width: 380px;
  width: 90%;
  position: relative;
  border: 1px solid rgba(255, 165, 0, 0.3);
  text-align: center;
}

.modal-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.premium-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 1rem;
  color: #ffa500;
}

.premium-icon svg {
  width: 100%;
  height: 100%;
}

h2 {
  color: #ffa500;
  margin: 0 0 0.75rem;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.3;
}

.feature-highlight {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  line-height: 1.5;
  margin: 0 0 1.5rem;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  text-align: left;
}

.benefits-list li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
  line-height: 1.4;
  padding: 0.5rem 0;
}

.benefit-icon {
  color: #ffa500;
  font-size: 1.4rem;
  width: 28px;
  text-align: center;
  flex-shrink: 0;
}

.price-section {
  margin-bottom: 1rem;
}

.price {
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
}

.period {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
}

.upgrade-button {
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #ffa500, #ff8c00);
  border: none;
  border-radius: 10px;
  color: black;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.upgrade-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(255, 165, 0, 0.4);
}

.upgrade-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  border-top-color: black;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  color: #ff6b6b;
  font-size: 0.85rem;
  margin: 0.75rem 0 0;
  padding: 0.5rem;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 6px;
}

.cancel-note {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  margin: 1rem 0 0;
}
</style>
